'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { isApiError } from '@/lib/api/client';
import { reviewApi } from './review.api';
import { reviewContainerKeys, reviewKeys } from './review.queries';
import type { RejectStageRequest } from '../types';

export function useApproveStageMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('review');

  return useMutation({
    mutationFn: (registrationId: number) =>
      reviewApi.advanceStage(registrationId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('approveSuccess')
    },
    onError: (error) => {
      // 409 `unexpected_stage`: the container already moved elsewhere. The
      // global handler toasts the server's message; refetch so the row shows
      // where it really is.
      if (isApiError(error) && error.status === 409) {
        queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewContainerKeys.all
      });
      queryClient.invalidateQueries({
        queryKey: reviewKeys.all
      });
    }
  });
}

export function useRejectStageMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('review');

  return useMutation({
    mutationFn: ({
      registrationId,
      data
    }: {
      registrationId: number;
      data: RejectStageRequest;
    }) => reviewApi.rejectStage(registrationId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('rejectSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: reviewContainerKeys.all
      });
      queryClient.invalidateQueries({
        queryKey: reviewKeys.all
      });
    }
  });
}
